import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  Search, X, Eye, UserX, UserCheck, Trash2, ShieldCheck, ChevronDown,
  ShieldOff, Mail, AlertTriangle, History, Users as UsersIcon,
  Phone as PhoneIcon, CheckCircle2, Clock, Undo2, MessageSquare,
} from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaInput from "@/components/jea/JeaInput";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import Skeleton from "@/components/jea/Skeleton";
import UserDetailDrawer from "@/components/admin/UserDetailDrawer";
import RecoveryActionsModal from "@/pages/admin/components/RecoveryActionsModal";
import RecoveryAuditLogPanel from "@/pages/admin/components/RecoveryAuditLogPanel";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { relativeTime, centsToUSD } from "@/lib/dates";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "standard", label: "Standard" },
  { key: "pro", label: "PRO" },
  { key: "admin", label: "Admin" },
  { key: "suspended", label: "Suspended" },
];

// Iteration 34 — Phone-presence filter (chips row above the table).
const PHONE_FILTERS = [
  { key: "all", label: "All" },
  { key: "with", label: "With Phone" },
  { key: "email_only", label: "Email Only" },
];

const PhoneCell = ({ phoneMasked, phoneVerified }) => {
  if (!phoneMasked) {
    return <span className="text-text-muted jea-mono text-[13px]" data-testid="admin-user-phone-none">—</span>;
  }
  const Icon = phoneVerified ? CheckCircle2 : Clock;
  const iconClass = phoneVerified ? "text-success" : "text-warning";
  const title = phoneVerified ? "Phone verified" : "Phone pending verification";
  return (
    <span className="inline-flex items-center gap-1.5 text-text-secondary jea-mono text-[13px]">
      <span className="whitespace-nowrap">{phoneMasked}</span>
      <Icon size={13} className={iconClass} aria-label={title}>
        <title>{title}</title>
      </Icon>
    </span>
  );
};

const RoleBadge = ({ role }) => {
  const tone =
    role === "ADMIN" ? "bg-danger/15 text-danger border-danger/40"
    : role === "PRO" ? "bg-accent-pink/15 text-accent-pink border-accent-pink/40"
    : "bg-bg-tertiary text-text-secondary border-border-default";
  return (
    <span className={cn("inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border", tone)}>
      {role}
    </span>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    active: { tone: "bg-success/15 text-success border-success/40", label: "Active" },
    suspended: { tone: "bg-danger/15 text-danger border-danger/40", label: "Suspended" },
    deleted: { tone: "bg-bg-tertiary text-text-muted border-border-default", label: "Deleted" },
  };
  const cfg = map[status] || map.active;
  return (
    <span className={cn("inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border", cfg.tone)}>
      {cfg.label}
    </span>
  );
};

const AdminUsersPage = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  // Iteration 34 — Phone filter + deleted-visibility toggle.
  const [phoneFilter, setPhoneFilter] = useState("all");
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  // Phase 10C — Recovery actions
  const [recoveryTarget, setRecoveryTarget] = useState(null);     // {user, action}
  const [auditRefreshKey, setAuditRefreshKey] = useState(0);
  const [tab, setTab] = useState("users"); // "users" | "audit"

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (filter !== "all") params.set("filter", filter);
      if (phoneFilter !== "all") params.set("has_phone", phoneFilter);
      if (showDeleted) params.set("include_deleted", "true");
      params.set("limit", "50");
      const { data } = await apiClient.get(`/admin/users?${params.toString()}`);
      setUsers(data.users || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, phoneFilter, showDeleted]);
  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const action = async (u, kind) => {
    setPendingAction(`${u.id}:${kind}`);
    try {
      let url = "";
      let method = "post";
      if (kind === "suspend") url = `/admin/users/${u.id}/suspend`;
      if (kind === "unsuspend") url = `/admin/users/${u.id}/unsuspend`;
      if (kind === "delete") { url = `/admin/users/${u.id}`; method = "delete"; }
      if (kind === "promote") url = `/admin/users/${u.id}/promote-admin`;
      if (kind === "demote") url = `/admin/users/${u.id}/demote-admin`;
      // Iteration 34 — Restore a soft-deleted user.
      if (kind === "restore") url = `/admin/users/${u.id}/restore`;
      const req = method === "delete" ? apiClient.delete(url) : apiClient.post(url, {});
      const { data } = await req;
      toast.success(data.detail || "Done");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingAction(null);
      setConfirmingDelete(null);
    }
  };

  const filteredCount = users.length;

  return (
    <div className="space-y-5" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="jea-caption">Platform Access</div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">User Management</h1>
          <p className="text-text-secondary text-[14px]">
            {total} total {total === 1 ? "user" : "users"} · showing {filteredCount}
          </p>
        </div>
      </div>

      {/* Tabs — Users | Recovery Audit Log */}
      <div className="flex items-center gap-1 border-b border-border-default" data-testid="admin-users-tabs">
        <button
          onClick={() => setTab("users")}
          className={cn(
            "px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2",
            tab === "users"
              ? "border-success text-white"
              : "border-transparent text-text-secondary hover:text-white"
          )}
          data-testid="admin-users-tab-users"
        >
          <UsersIcon className="w-4 h-4" /> Users
        </button>
        <button
          onClick={() => setTab("audit")}
          className={cn(
            "px-4 py-2.5 text-[13px] font-semibold border-b-2 -mb-px transition-colors flex items-center gap-2",
            tab === "audit"
              ? "border-success text-white"
              : "border-transparent text-text-secondary hover:text-white"
          )}
          data-testid="admin-users-tab-audit"
        >
          <History className="w-4 h-4" /> Recovery Audit Log
        </button>
      </div>

      {tab === "audit" && <RecoveryAuditLogPanel refreshKey={auditRefreshKey} />}

      {tab !== "audit" && (
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="w-[320px]">
          <JeaInput
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<Search size={14} />}
            data-testid="admin-users-search"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="admin-users-filters">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={cn(
                "h-9 px-3 rounded-full border text-[12px] font-semibold transition-all",
                filter === f.key
                  ? "bg-accent-pink text-[#091424] border-accent-pink"
                  : "bg-bg-secondary border-border-default text-text-secondary hover:text-white",
              )}
              data-testid={`admin-users-filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      )}

      {tab !== "audit" && (
        <div
          className="flex items-center gap-2 flex-wrap"
          data-testid="admin-users-secondary-filters"
        >
          <span className="jea-caption text-text-muted mr-1">Phone</span>
          {PHONE_FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setPhoneFilter(f.key)}
              className={cn(
                "h-8 px-3 rounded-full border text-[12px] font-semibold transition-all inline-flex items-center gap-1.5",
                phoneFilter === f.key
                  ? "bg-success/15 text-success border-success/60"
                  : "bg-bg-secondary border-border-default text-text-secondary hover:text-white",
              )}
              data-testid={`admin-users-phone-filter-${f.key}`}
            >
              {f.key === "with" && <PhoneIcon size={12} />}
              {f.label}
            </button>
          ))}
          <span className="w-px h-5 bg-border-default mx-2" />
          <button
            type="button"
            onClick={() => setShowDeleted((v) => !v)}
            className={cn(
              "h-8 px-3 rounded-full border text-[12px] font-semibold transition-all inline-flex items-center gap-1.5",
              showDeleted
                ? "bg-danger/15 text-danger border-danger/60"
                : "bg-bg-secondary border-border-default text-text-secondary hover:text-white",
            )}
            data-testid="admin-users-toggle-deleted"
            aria-pressed={showDeleted}
          >
            <Trash2 size={12} />
            {showDeleted ? "Hide Deleted" : "Show Deleted"}
          </button>
        </div>
      )}

      {tab !== "audit" && loading && (
        <JeaCard className="p-4 space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-14" />)}
        </JeaCard>
      )}
      {tab !== "audit" && !loading && users.length === 0 && (
        <EmptyState
          icon={Search}
          title="No users match"
          description="Try clearing filters or searching by a different term."
          testid="admin-users-empty"
        />
      )}
      {tab !== "audit" && !loading && users.length > 0 && (
        <JeaCard className="p-0 overflow-hidden" data-testid="admin-users-table">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-default">
                  <th className="jea-caption text-text-secondary p-4 text-left">Name</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Email</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Phone</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Role</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Plan</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Judgments</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Spend YTD</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Status</th>
                  <th className="jea-caption text-text-secondary p-4 text-left">Joined</th>
                  <th className="jea-caption text-text-secondary p-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => {
                  const isSuspended = u.status === "suspended";
                  const isDeleted = u.status === "deleted";
                  const isSelf = u.id === me?.id;
                  const debtorLine = u.slot_max
                    ? `${u.debtor_count}/${u.slot_max}`
                    : String(u.debtor_count || 0);
                  const planLine = u.subscription
                    ? (u.subscription.plan_type === "pro"
                        ? `PRO ${u.subscription.path || ""}`.trim()
                        : `Standard · ${{ A: "3M", B: "6M", C: "12M" }[u.subscription.path] || u.subscription.path || "?"}`)
                    : "—";
                  return (
                    <motion.tr
                      key={u.id}
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18, delay: i * 0.02 }}
                      className={cn(
                        "border-b border-border-default hover:bg-bg-tertiary hover:border-l-2 hover:border-l-accent-pink transition-colors",
                        isDeleted && "opacity-60",
                      )}
                      data-testid={`admin-user-row-${u.id}`}
                    >
                      <td className="p-4">
                        <button
                          type="button"
                          onClick={() => setSelectedId(u.id)}
                          className={cn(
                            "text-white font-semibold hover:text-accent-pink transition-colors text-left",
                            isDeleted && "line-through decoration-danger/60",
                          )}
                          data-testid={`admin-user-name-${u.id}`}
                        >
                          {u.full_name}
                        </button>
                      </td>
                      <td className={cn("p-4 text-text-secondary text-[13px]", isDeleted && "line-through")}>{u.email || <span className="text-text-muted">—</span>}</td>
                      <td className="p-4" data-testid={`admin-user-phone-${u.id}`}>
                        <PhoneCell phoneMasked={u.phone_masked} phoneVerified={u.phone_verified} />
                      </td>
                      <td className="p-4"><RoleBadge role={u.role} /></td>
                      <td className="p-4 text-text-secondary text-[13px]">{planLine}</td>
                      <td className="p-4 text-text-secondary text-[13px] jea-mono">{debtorLine}</td>
                      <td className="p-4 jea-mono text-accent-pink font-bold text-[13px]">{centsToUSD(u.total_spend_cents || 0)}</td>
                      <td className="p-4"><StatusPill status={u.status} /></td>
                      <td className="p-4 jea-date text-[13px]">{relativeTime(u.created_at)}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-1 justify-end">
                          <button
                            type="button"
                            onClick={() => setSelectedId(u.id)}
                            className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-accent-pink hover:border-accent-pink/50 transition-colors inline-flex items-center justify-center"
                            title="View details"
                            data-testid={`admin-user-view-${u.id}`}
                          >
                            <Eye size={13} />
                          </button>
                          {isDeleted && !isSelf && (
                            <button
                              type="button"
                              disabled={pendingAction === `${u.id}:restore`}
                              onClick={() => action(u, "restore")}
                              className="w-8 h-8 rounded-[6px] border border-success/50 text-success hover:bg-success/10 transition-colors inline-flex items-center justify-center"
                              title="Restore user"
                              data-testid={`admin-user-restore-${u.id}`}
                            >
                              <Undo2 size={13} />
                            </button>
                          )}
                          {!u.is_admin && !isDeleted && !isSelf && (
                            <button
                              type="button"
                              disabled={pendingAction === `${u.id}:${isSuspended ? "unsuspend" : "suspend"}`}
                              onClick={() => action(u, isSuspended ? "unsuspend" : "suspend")}
                              className={cn(
                                "w-8 h-8 rounded-[6px] border transition-colors inline-flex items-center justify-center",
                                isSuspended
                                  ? "border-success/40 text-success hover:bg-success/10"
                                  : "border-warning/40 text-warning hover:bg-warning/10",
                              )}
                              title={isSuspended ? "Reactivate" : "Suspend"}
                              data-testid={`admin-user-suspend-${u.id}`}
                            >
                              {isSuspended ? <UserCheck size={13} /> : <UserX size={13} />}
                            </button>
                          )}
                          {!u.is_admin && !isDeleted && !isSelf && (
                            <button
                              type="button"
                              disabled={pendingAction === `${u.id}:delete`}
                              onClick={() => setConfirmingDelete(u)}
                              className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-danger hover:border-danger/60 transition-colors inline-flex items-center justify-center"
                              title="Delete"
                              data-testid={`admin-user-delete-${u.id}`}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                          {!isDeleted && !isSelf && (
                            <>
                              <button
                                type="button"
                                onClick={() => setRecoveryTarget({ user: u, action: "reset-2fa" })}
                                className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-warning hover:border-warning/60 transition-colors inline-flex items-center justify-center"
                                title="Reset 2FA"
                                data-testid={`admin-user-reset-2fa-${u.id}`}
                              >
                                <ShieldOff size={13} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setRecoveryTarget({ user: u, action: "send-password-reset" })}
                                className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-success hover:border-success/60 transition-colors inline-flex items-center justify-center"
                                title="Send Password Reset (Email)"
                                data-testid={`admin-user-send-reset-${u.id}`}
                              >
                                <Mail size={13} />
                              </button>
                              {/* Phase 10F — SMS password reset. Enabled only
                                  when the user has a verified phone. */}
                              <button
                                type="button"
                                onClick={() => u.phone_verified && setRecoveryTarget({ user: u, action: "send-password-reset-sms" })}
                                disabled={!u.phone_verified}
                                className={cn(
                                  "w-8 h-8 rounded-[6px] border transition-colors inline-flex items-center justify-center",
                                  u.phone_verified
                                    ? "border-border-default text-text-secondary hover:text-success hover:border-success/60"
                                    : "border-border-default text-text-muted opacity-40 cursor-not-allowed",
                                )}
                                title={u.phone_verified
                                  ? "Send Password Reset (SMS)"
                                  : "User has no verified phone number"}
                                data-testid={`admin-user-send-reset-sms-${u.id}`}
                              >
                                <MessageSquare size={13} />
                              </button>
                              <button
                                type="button"
                                onClick={() => setRecoveryTarget({ user: u, action: "full-recovery" })}
                                className="w-8 h-8 rounded-[6px] border border-border-default text-text-secondary hover:text-danger hover:border-danger/60 transition-colors inline-flex items-center justify-center"
                                title="Full account recovery (reset 2FA + password link)"
                                data-testid={`admin-user-full-recovery-${u.id}`}
                              >
                                <AlertTriangle size={13} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </JeaCard>
      )}

      <RecoveryActionsModal
        user={recoveryTarget?.user}
        action={recoveryTarget?.action}
        onClose={() => setRecoveryTarget(null)}
        onCompleted={() => {
          setAuditRefreshKey((k) => k + 1);
          load();
        }}
      />

      <UserDetailDrawer
        userId={selectedId}
        open={!!selectedId}
        currentAdminId={me?.id}
        onClose={() => setSelectedId(null)}
        onAction={async (kind, u) => {
          if (kind === "delete") { setSelectedId(null); setConfirmingDelete(u); return; }
          await action(u, kind);
          if (kind === "delete") setSelectedId(null);
        }}
      />

      <AnimatePresence>
        {confirmingDelete && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-[12px] z-[95]"
              onClick={() => setConfirmingDelete(null)}
            />
            <motion.div
              initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
              className="fixed inset-0 z-[96] flex items-center justify-center px-4"
              data-testid="admin-user-delete-modal"
            >
              <div
                className="w-full max-w-[480px] bg-bg-secondary border border-danger/40 rounded-[16px] p-8 shadow-elevated"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-full bg-danger/15 border border-danger/40 flex items-center justify-center shrink-0">
                    <Trash2 size={20} className="text-danger" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-[18px]">Delete this user?</h2>
                    <p className="text-text-secondary text-[13px] mt-2 leading-6">
                      This will soft-delete <strong className="text-white">{confirmingDelete.email}</strong>.
                      Their data is preserved for audit but the account will be revoked. Active
                      subscriptions must be canceled first.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(null)}
                    className="p-2 text-text-secondary hover:text-white"
                  >
                    <X size={16} />
                  </button>
                </div>
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setConfirmingDelete(null)}
                    className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white hover:bg-bg-tertiary text-[14px] font-semibold transition-colors"
                    data-testid="admin-user-delete-cancel"
                  >
                    Cancel
                  </button>
                  <JeaButton
                    variant="danger"
                    onClick={() => action(confirmingDelete, "delete")}
                    loading={pendingAction === `${confirmingDelete.id}:delete`}
                    data-testid="admin-user-delete-confirm"
                  >
                    Delete Account
                  </JeaButton>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminUsersPage;
